import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ListingCard from '../components/ListingCard';
import { listingsApi } from '../api/client';
import { getCityBySlug, CITIES } from '../data/cities';
import { CATEGORIES } from '../data/mock';
import { ChevronLeft, ChevronRight, Lock, EyeOff, ShieldCheck, Loader2, MapPin } from 'lucide-react';

const PAGE_SIZE = 30;
const RADIUS = 30;

const CityLandingPage = () => {
  const { slug } = useParams();
  const city = getCityBySlug(slug);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('Alle');

  useEffect(() => { setPage(1); setCategory('Alle'); window.scrollTo(0, 0); }, [slug]);
  useEffect(() => { setPage(1); }, [category]);

  useEffect(() => {
    if (!city) return;
    const prev = document.title;
    document.title = `${category !== 'Alle' ? category + ' ' : 'Sex Inserate '}${city.name} — matchdate.ch`;
    return () => { document.title = prev; };
  }, [city, category]);

  useEffect(() => {
    if (!city) return;
    let cancelled = false;
    setLoading(true);
    const params = { near_city: city.name, radius_km: RADIUS, page, page_size: PAGE_SIZE };
    if (category !== 'Alle') params.category = category;
    listingsApi.list(params)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setData({ items: [], total: 0, total_pages: 1 }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [city, page, category]);

  const goPage = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  if (!city) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="city-not-found">
        <p className="text-[#b8a9a3] mb-4">Stadt nicht gefunden.</p>
        <Link to="/inserate" className="text-[#c19a8a] hover:text-white">Zu allen Inseraten</Link>
      </div>
    );
  }

  const otherCities = CITIES.filter((c) => c.slug !== city.slug);

  return (
    <div className="min-h-screen bg-[#0f0a0c]" data-testid="city-landing-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-60"
          style={{ background: 'radial-gradient(ellipse at top left, #4a1f3a 0%, transparent 50%), radial-gradient(ellipse at bottom right, #1c2a4a 0%, transparent 50%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 pb-8 sm:pb-10">
          <nav className="text-xs text-[#9a8a83] mb-4 flex items-center gap-2 flex-wrap" data-testid="city-breadcrumb">
            <Link to="/inserate" className="hover:text-white">Inserate</Link>
            <span className="text-[#5a4d48]">/</span>
            <Link to={`/inserate?region=${encodeURIComponent(city.canton)}`} className="hover:text-white">{city.canton}</Link>
            <span className="text-[#5a4d48]">/</span>
            <span className="text-[#c19a8a]">{city.name}</span>
          </nav>
          <p className="text-xs tracking-[0.3em] uppercase text-[#c19a8a] mb-3">{city.canton} · 18+ · Diskret</p>
          <h1 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight" data-testid="city-title">
            Sex Inserate <span className="italic text-[#e8a3b8]">{city.name}</span>
          </h1>
          <p className="mt-4 sm:mt-5 text-sm sm:text-lg text-[#b8a9a3] max-w-2xl">
            Entdecke diskrete Sex Inserate und Kontaktanzeigen in {city.name} und Umgebung ({RADIUS} km).
            Aktuelle Erotik-Inserate, Escorts, Massagen und mehr — seriös, diskret & tagesaktuell.
            {data.total > 0 ? ` ${data.total} Inserate in deiner Nähe.` : ''}
          </p>
          <div className="mt-6 flex flex-wrap gap-4 text-xs text-[#9a8a83]">
            <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#c19a8a]" /> Verschlüsselt</span>
            <span className="inline-flex items-center gap-1.5"><EyeOff className="w-3.5 h-3.5 text-[#c19a8a]" /> Kein Tracking</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#c19a8a]" /> Werbefrei</span>
          </div>
        </div>
      </section>

      {/* Category pills: "[Kategorie] [Stadt]" */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-2 mt-2">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin" data-testid="city-category-pills">
          <button onClick={() => setCategory('Alle')}
            data-testid="city-category-alle"
            className={`whitespace-nowrap px-4 py-2 rounded-full text-xs tracking-wide border transition-colors duration-200 ${
              category === 'Alle'
                ? 'bg-[#a83a5b] border-[#a83a5b] text-white'
                : 'bg-transparent border-[#2a1f24] text-[#b8a9a3] hover:border-[#a83a5b] hover:text-white'
            }`}>
            Alle Inserate
          </button>
          {CATEGORIES.filter((c) => c !== 'Alle').map((c) => (
            <button key={c} onClick={() => setCategory(c)}
              data-testid={`city-category-${c}`}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-xs tracking-wide border transition-colors duration-200 ${
                category === c
                  ? 'bg-[#a83a5b] border-[#a83a5b] text-white'
                  : 'bg-transparent border-[#2a1f24] text-[#b8a9a3] hover:border-[#a83a5b] hover:text-white'
              }`}>
              {c} {city.name}
            </button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="flex items-baseline justify-between mb-5 pt-2">
          <h2 className="font-serif text-xl sm:text-2xl text-white" data-testid="city-results-heading">
            {category !== 'Alle' ? `${category} ${city.name}` : `Inserate in ${city.name}`}
          </h2>
          <span className="text-sm text-[#7a6b65]">{data.total} Inserate</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#9a8a83]">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#2a1f24] rounded-xl" data-testid="city-empty">
            <p className="text-[#9a8a83] mb-4">Aktuell keine Inserate in {city.name} und Umgebung.</p>
            <Link to="/inserate" className="text-[#c19a8a] hover:text-white">Alle Inserate der Schweiz ansehen</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="city-listings-grid">
            {data.items.map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        )}

        {data.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-12 flex-wrap">
            <button onClick={() => goPage(Math.max(1, page - 1))} disabled={page === 1}
              data-testid="city-prev-page"
              className="px-3 py-2 rounded-md border border-[#2a1f24] text-[#b8a9a3] hover:text-white hover:border-[#a83a5b] disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" /> Zurück
            </button>
            {Array.from({ length: data.total_pages }, (_, i) => i + 1).map((p) => (
              <button key={p} onClick={() => goPage(p)}
                className={`w-9 h-9 rounded-md text-sm transition-colors ${
                  p === page ? 'bg-[#a83a5b] text-white' : 'border border-[#2a1f24] text-[#b8a9a3] hover:text-white hover:border-[#a83a5b]'
                }`}>
                {p}
              </button>
            ))}
            <button onClick={() => goPage(Math.min(data.total_pages, page + 1))} disabled={page === data.total_pages}
              data-testid="city-next-page"
              className="px-3 py-2 rounded-md border border-[#2a1f24] text-[#b8a9a3] hover:text-white hover:border-[#a83a5b] disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1">
              Weiter <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>

      {/* Other cities */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <h3 className="font-serif text-lg text-white mb-4">Sex Inserate in anderen Städten</h3>
        <div className="flex flex-wrap gap-2" data-testid="other-cities">
          {otherCities.map((c) => (
            <Link key={c.slug} to={`/sex-inserate/${c.slug}`}
              className="px-3 py-1.5 rounded-full bg-[#1a1417] border border-[#2a1f24] text-xs text-[#c19a8a] hover:text-white hover:border-[#a83a5b] transition-colors inline-flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {c.name}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default CityLandingPage;
